import { gql, type TypedDocumentNode } from '@apollo/client'
import { useMutation } from '@apollo/client/react'
import { RatingFormField } from '@repo/shared-client/components/fields'
import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@repo/shared-client/components/ui'
import { Form, useForm, useFormState } from '@repo/shared-client/form'
import { useEffect, useRef } from 'react'
import toast from 'react-hot-toast'
import SFTextAreaEmojiField from 'src/components/primatives/sf-text-area-emoji-field/sf-text-area-emoji-field'
import { ReviewInfo } from 'src/lib/fragments'
import { useTranslation } from 'src/lib/intl/react'
import type {
  CreateReviewMutation,
  CreateReviewMutationVariables,
  UpdateReviewMutation,
  UpdateReviewMutationVariables,
} from 'types/graphql'

export const CREATE_REVIEW: TypedDocumentNode<CreateReviewMutation, CreateReviewMutationVariables> = gql`
  mutation CreateReview($input: CreateReviewInput!) {
    createReview(input: $input) {
      ...ReviewInfo
    }
  }
  ${ReviewInfo}
`

export const UPDATE_REVIEW: TypedDocumentNode<UpdateReviewMutation, UpdateReviewMutationVariables> = gql`
  mutation UpdateReview($reviewId: String!, $input: UpdateReviewInput!) {
    updateReview(reviewId: $reviewId, input: $input) {
      ...ReviewInfo
    }
  }
  ${ReviewInfo}
`

type ReviewFormValues = {
  rating: number | null
  content: string
}

type ReviewCreateEditDialogProps = {
  open?: boolean
  onOpenChange?: (details: { open: boolean }) => void
  placeIdOrSlug?: string
  publicationIdOrSlug?: string
  vendorMarketIdOrSlug?: string
  vendorMarketItemIdOrSlug?: string
  parentId?: string
  review?: {
    id: string
    rating: number
    content: string
  }
  onReviewChange?: (data: object) => void
}

const ReviewCreateEditDialog = (props: ReviewCreateEditDialogProps) => {
  const { t } = useTranslation()
  const ratingFieldRef = useRef<HTMLFieldSetElement | null>(null)
  const contentFieldRef = useRef<HTMLTextAreaElement | null>(null)
  const formMethods = useForm<ReviewFormValues>({
    defaultValues: {
      rating: props?.review?.rating ?? null,
      content: props?.review?.content ?? '',
    },
  })
  const { open, onOpenChange, onReviewChange } = props
  const { isDirty } = useFormState({ control: formMethods.control })

  useEffect(() => {
    formMethods.reset({
      rating: props?.review?.rating ?? null,
      content: props?.review?.content ?? '',
    })
  }, [props?.review?.rating, props?.review?.content, formMethods])

  const [CreateReview] = useMutation(CREATE_REVIEW, {
    onCompleted: (data) => {
      toast.success(t('Review Created'))
      onReviewChange?.(data.createReview)
      handleClose()
    },
    onError: (error) => {
      toast.error(error.message)
    },
  })

  const [UpdateReview] = useMutation(UPDATE_REVIEW, {
    onCompleted: (data) => {
      toast.success(t('Review update complete'))
      onReviewChange?.(data.updateReview)
      handleClose()
    },
    onError: (error) => {
      toast.error(error.message)
    },
  })

  const onSubmit = (fieldData: ReviewFormValues) => {
    if (props?.review) {
      UpdateReview({
        variables: {
          reviewId: props?.review.id,
          input: {
            rating: props?.parentId ? 0 : Number.parseInt(String(fieldData.rating ?? 0), 10),
            content: fieldData.content,
          },
        },
      })
    } else {
      CreateReview({
        variables: {
          input: {
            placeId: props?.placeIdOrSlug,
            publicationId: props?.publicationIdOrSlug,
            vendorMarketId: props?.vendorMarketIdOrSlug,
            vendorMarketItemId: props?.vendorMarketItemIdOrSlug,
            parentId: props?.parentId,
            rating: props?.parentId ? 0 : Number.parseInt(String(fieldData.rating ?? 0), 10),
            content: fieldData.content,
          },
        },
      })
    }
  }

  const closeDialog = () => {
    onOpenChange?.({ open: false })
  }

  const handleClose = () => {
    formMethods.reset({
      rating: props.review?.rating ?? null,
      content: props.review?.content ?? '',
    })
    closeDialog()
  }

  useEffect(() => {
    if (open) {
      const id = requestAnimationFrame(() => {
        const target =
          props?.parentId === null || props?.parentId === undefined ? ratingFieldRef.current : contentFieldRef.current
        target?.focus?.()
      })
      return () => cancelAnimationFrame(id)
    }
  }, [open, props?.parentId])

  return (
    <Dialog
      disablePointerDismissal
      modal={false}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) {
          formMethods.reset({
            rating: props.review?.rating ?? null,
            content: props.review?.content ?? '',
          })
        }
        onOpenChange?.({ open: nextOpen })
      }}
      open={open}
    >
      <DialogContent className="max-h-[90dvh] max-w-sm sm:max-w-md" initialFocus={false}>
        <Form className="contents" formMethods={formMethods} onSubmit={onSubmit}>
          <DialogHeader>
            <DialogTitle>{props?.review ? t('Edit Review') : t('Create Review')}</DialogTitle>
          </DialogHeader>

          <div className="flex flex-col gap-4">
            {(props?.parentId === null || props?.parentId === undefined) && (
              <RatingFormField
                label={t('Review Rating')}
                name="rating"
                ratingProps={{ size: 'sm', variant: 'default' }}
                ref={ratingFieldRef}
                required
                rules={{
                  required: t('You must provide a rating'),
                }}
              />
            )}

            <SFTextAreaEmojiField
              label={t('Review Feedback')}
              name="content"
              ref={contentFieldRef}
              required
              rules={{
                maxLength: {
                  value: 1024,
                  message: t('Max message length of 1024 characters exceeded'),
                },
              }}
              textareaProps={{ placeholder: t('Please provide your review here...') }}
            />
          </div>

          <DialogFooter className="flex flex-row justify-end gap-2">
            <Button aria-label={t('Close')} onClick={handleClose} type="button" variant="outline">
              {t('Cancel')}
            </Button>
            <Button disabled={!isDirty} type="submit">
              {t('Save')}
            </Button>
          </DialogFooter>
        </Form>
      </DialogContent>
    </Dialog>
  )
}

export default ReviewCreateEditDialog
